import React, { forwardRef, useState } from 'react'
import { X, Eye, EyeOff } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  state?: 'default' | 'focus' | 'error' | 'disable' | 'fill'
  showClearButton?: boolean
  onClear?: () => void
  showPasswordToggle?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    size = 'medium',
    state = 'default',
    showClearButton = true,
    showPasswordToggle = false,
    onClear,
    className = '',
    disabled,
    value,
    onChange,
    onFocus,
    onBlur,
    type = 'text',
    ...props
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false)
    const [internalValue, setInternalValue] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    // Use controlled or uncontrolled value
    const displayValue = value !== undefined ? value : internalValue
    const hasValue = String(displayValue).length > 0

    // Determine actual input type
    const actualType = type === 'password' && showPassword ? 'text' : type
    
    // Determine actual state
    const actualState = disabled ? 'disable' : 
                       hasValue ? 'fill' : 
                       isFocused ? 'focus' : 
                       state

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value)
      }
      onChange?.(e)
    }

    const handleClear = () => {
      if (value === undefined) {
        setInternalValue('')
      }
      onClear?.()
      // Create synthetic event for onChange
      const input = document.querySelector(`input[data-size="${size}"]`) as HTMLInputElement
      if (input && onChange) {
        const event = new Event('input', { bubbles: true })
        Object.defineProperty(event, 'target', {
          value: { ...input, value: '' },
          writable: false
        })
        onChange(event as React.ChangeEvent<HTMLInputElement>)
      }
    }

    const sizeClasses = {
      xsmall: 'h-7 px-2 text-xs gap-1',
      small: 'h-[34px] px-3 text-sm gap-1.5',
      medium: 'h-10 px-3 text-base gap-1.5',
      large: 'px-3 text-lg gap-1.5'
    }

    const getStateStyles = (state: string) => {
      const stateMap = {
        default: {
          backgroundColor: designTokens.colors.white,
          borderColor: designTokens.colors.gray[1],
          color: designTokens.colors.gray[3]
        },
        focus: {
          backgroundColor: designTokens.colors.white,
          borderColor: designTokens.colors.info,
          color: designTokens.colors.gray[6]
        },
        error: {
          backgroundColor: designTokens.colors.white,
          borderColor: designTokens.colors.error,
          color: designTokens.colors.gray[6]
        },
        disable: {
          backgroundColor: designTokens.colors.gray[0],
          borderColor: designTokens.colors.gray[1],
          color: designTokens.colors.gray[3],
          cursor: 'not-allowed'
        },
        fill: {
          backgroundColor: designTokens.colors.white,
          borderColor: designTokens.colors.gray[1],
          color: designTokens.colors.gray[6]
        }
      }

      return stateMap[state as keyof typeof stateMap]
    }

    const iconSizeClasses = {
      xsmall: 'w-3.5 h-3.5',
      small: 'w-[18px] h-[18px]',
      medium: 'w-5 h-5',
      large: 'w-[22px] h-[22px]'
    }

    const stateStyle = getStateStyles(actualState)

    return (
      <div 
        className={`relative flex items-center border-2 rounded transition-all duration-150 ${sizeClasses[size]} ${className}`}
        style={{
          backgroundColor: stateStyle.backgroundColor,
          borderColor: stateStyle.borderColor,
        }}
      >
        <input
          ref={ref}
          type={actualType}
          disabled={disabled}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-size={size}
          className={`flex-1 bg-transparent outline-none ${disabled ? 'cursor-not-allowed' : ''}`}
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: size === 'large' ? designTokens.typography.fontSize['body-large'][0] :
                      size === 'medium' ? designTokens.typography.fontSize['body-medium'][0] :
                      size === 'small' ? designTokens.typography.fontSize['body-small'][0] :
                      designTokens.typography.fontSize['body-xsmall'][0],
            lineHeight: size === 'large' ? designTokens.typography.fontSize['body-large'][1].lineHeight :
                       size === 'medium' ? designTokens.typography.fontSize['body-medium'][1].lineHeight :
                       size === 'small' ? designTokens.typography.fontSize['body-small'][1].lineHeight :
                       designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
            color: stateStyle.color,
          }}
          {...props}
        />
        {showPasswordToggle && type === 'password' && !disabled && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="flex items-center justify-center transition-colors mr-1"
            style={{
              color: designTokens.colors.gray[3]
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.color = designTokens.colors.gray[4]
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.color = designTokens.colors.gray[3]
            }}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff className={iconSizeClasses[size]} />
            ) : (
              <Eye className={iconSizeClasses[size]} />
            )}
          </button>
        )}
        {showClearButton && hasValue && !disabled && type !== 'password' && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center justify-center transition-colors"
            style={{ 
              color: designTokens.colors.gray[3] 
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.color = designTokens.colors.gray[4]
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.color = designTokens.colors.gray[3]
            }}
            aria-label="Clear input"
          >
            <X className={iconSizeClasses[size]} />
          </button>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input